import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import { queryClient } from "@shared/api/queries/queryClient";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { useNavigate } from "react-router-dom";
import {
  exportBillCycleList,
  getBillCycleConsumerDashboard,
  getBillCycleDetails,
  getBillCycleDetailsForEdit,
  getBillCycleList,
  getBillSummary,
  getBillStatusChoices,
  getCategoryConfigMap,
  getConsumerList,
  getCycleWiseRun,
  getMonthlyStats,
  getPremiseList,
  getSubCategoryConfigMap,
  getTerritoryConfigMap,
  submitBillCycle,
  updateBillCycle,
} from "./api";
import { useState } from "react";
import { downloadCSV } from "@shared/utils/utils";
import { toast } from "@shared/ui/use-toast";

export const useBillCycleConsumerDashboard = (params: Record<string, any>) => {
  return useSmartQuery(
    [QueryKeyFactory.module.bx.billCycle.billCycleDashboard(), params],
    () => getBillCycleConsumerDashboard(params),
  );
};

// Hook for Bill Cycle List with pagination
export const useBillCycleList = (queryParams: Record<string, any>) => {
  return useSmartQuery(
    [QueryKeyFactory.module.bx.billCycle.billCycleList(), queryParams],
    () => getBillCycleList(queryParams),
    {
      staleTime: 0,
      refetchOnMount: true,
    },
  );
};

export const useBillCycleExport = (queryParams: Record<string, any> = {}) => {
  const [isExporting, setIsExporting] = useState(false);

  const exportMutation = useSmartQuery(
    [QueryKeyFactory.module.bx.billCycle.exportList(), queryParams],
    async () => {
      setIsExporting(true);
      try {
        const blob = await exportBillCycleList(queryParams);
        toast({
          title: "Export Initiated",
          description: blob?.message,
          variant: "default",
        });
        return { success: true };
      } catch (error: any) {
        toast({
          title: `Failed to export bill cycles: ${error.message}`,
          variant: "destructive",
        });
        throw error;
      } finally {
        setIsExporting(false);
      }
    },
    {
      enabled: false,
    },
  );

  const exportCSV = () => {
    exportMutation.refetch();
  };

  return {
    exportCSV,
    isExporting: isExporting || exportMutation.isFetching,
    error: exportMutation.error,
    isError: exportMutation.isError,
  };
};

export const useBillCycleDetails = (billCycleId: string | number) => {
  return useSmartQuery(
    [QueryKeyFactory.module.bx.billCycle.billCycleDetails(), billCycleId],
    () => getBillCycleDetails(billCycleId),
    {
      enabled: !!billCycleId,
    },
  );
};

export const useBillCycleDetailsForEdit = (billCycleId: string | number) => {
  return useSmartQuery(
    [QueryKeyFactory.module.bx.billCycle.billCycleDetailsEdit(), billCycleId],
    () => getBillCycleDetailsForEdit(billCycleId),
    {
      enabled: !!billCycleId,
    },
  );
};

export const useCycleWiseRun = (
  billCycleId: string | number,
  page: number,
  search: string,
) => {
  return useSmartQuery(
    [
      QueryKeyFactory.module.bx.billCycle.cycleWiseRunList(),
      billCycleId,
      page,
      search,
    ],
    () => getCycleWiseRun(billCycleId, page, search),
  );
};

export const useMonthlyStats = (billCycleId: string | number) => {
  return useSmartQuery(
    [QueryKeyFactory.module.bx.billCycle.monthlyStats(), billCycleId],
    () => getMonthlyStats(billCycleId),
  );
};

export const useCategoryConfigMap = () => {
  return useSmartQuery(
    [QueryKeyFactory.module.bx.billCycle.category(), getRemoteUtilityId()],
    () => getCategoryConfigMap(),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    },
  );
};

export const useBillStatusChoices = () => {
  return useSmartQuery(
    [QueryKeyFactory.module.bx.choices("bill_status"), getRemoteUtilityId()],
    () => getBillStatusChoices(),
  );
};

export const useSubCategoryConfigMap = (codes: string[]) => {
  return useSmartQuery(
    [QueryKeyFactory.module.bx.bulkBillGeneration.subCategoryList(), codes],
    () => getSubCategoryConfigMap(codes),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    },
  );
};

export const useConsumerList = (
  queryParams: Record<string, any>,
  billCycleId?: string | number,
  isEnabled?: boolean,
) => {
  return useSmartQuery(
    [
      QueryKeyFactory.module.bx.billCycle.consumerList(),
      queryParams,
      billCycleId,
    ],
    () => getConsumerList(queryParams, billCycleId),
    {
      enabled: isEnabled,
    },
  );
};

export const useExportConsumerList = (
  queryParams: Record<string, any>,
  billCycleId?: string | number,
  isEnabled?: boolean,
) => {
  return useSmartQuery(
    [
      QueryKeyFactory.module.bx.billCycle.consumerList(),
      queryParams,
      billCycleId,
    ],
    () => getConsumerList(queryParams, billCycleId),
    {
      staleTime: 0,
      enabled: isEnabled,
    },
  );
};
export const useBillSummary = () => {
  return useSmartQuery([QueryKeyFactory.module.bx.billCycle.summary()], () =>
    getBillSummary(),
  );
};

export const useSubmitBillCycle = () => {
  const navigate = useNavigate();
  return useSmartMutation<any, Error, any>(
    (payload: FormData) => submitBillCycle(payload),
    {
      onSuccess: () => {
        navigate("/bx/bill-setup/billcycle");
        queryClient.refetchQueries({
          queryKey: [["bx", "billing", "list", "billing-cycle-list"]],
        });
        queryClient.refetchQueries({
          queryKey: [["bx", "billing", "dashboard", "summary"]],
        });

        queryClient.refetchQueries({
          queryKey: [["bx", "list"]],
        });
        queryClient.refetchQueries({
          queryKey: [["bx", "billing", "details", "bill-cycle-details-Edit"]],
          exact: false,
        });
      },
    },
  );
};

export const useAreaList = (divisionCodes?: string[]) => {
  return useSmartQuery(
    [...QueryKeyFactory.module.bx.billCycle.areaList(), divisionCodes],
    () => getTerritoryConfigMap("area", divisionCodes),
  );
};
export const useSubAreaList = (areaList?: string[]) => {
  const safeAreaList = Array.isArray(areaList) ? areaList : [];

  return useSmartQuery(
    [...QueryKeyFactory.module.bx.billCycle.subAreaList(), safeAreaList],
    () => getTerritoryConfigMap("sub_area", safeAreaList),
    {
      enabled: safeAreaList.length > 0,
    },
  );
};

export const useZoneList = () => {
  return useSmartQuery(
    [...QueryKeyFactory.module.bx.billCycle.zoneList(), getRemoteUtilityId()],
    () => getTerritoryConfigMap("zone"),
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  );
};

export const useDivisionList = (zoneCodes: string[]) => {
  return useSmartQuery(
    [...QueryKeyFactory.module.bx.billCycle.divisionList(), zoneCodes],
    () => getTerritoryConfigMap("division", zoneCodes),
    {
      enabled: zoneCodes?.length > 0,
    },
  );
};

export const usePremiseList = (queryParams: Record<string, any>) => {
  return useSmartQuery(
    [QueryKeyFactory.module.bx.billCycle.premiseList(), queryParams],
    () => getPremiseList(queryParams),
  );
};

export const useUpdateBillCycle = (billCycleId: string) => {
  const navigate = useNavigate();
  return useSmartMutation<any, Error, any>(
    (payload: FormData) => updateBillCycle(payload, billCycleId),
    {
      onSuccess: () => {
        navigate("/bx/bill-setup/billcycle");
        queryClient.refetchQueries({
          queryKey: [["bx", "billing", "list", "billing-cycle-list"]],
        });

        queryClient.refetchQueries({
          queryKey: [["bx", "list"]],
        });
        queryClient.refetchQueries({
          queryKey: [["bx", "billing", "details", "bill-cycle-details-Edit"]],
          exact: false,
        });
      },
    },
  );
};
